import React, { useEffect, useState } from 'react';
import {
  TextField,
  Paper,
  Typography,
  Box,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../reduxStore/Store';
import MyButton from '../MyButton';
import { ButtonType, GeneriqueDataType, PERMISSION_PRIX_DEPT, PERMISSION_PRIX_REVIENT, TypeTiers } from '../../constants/enums';
import { usePermission } from '../../hooks/usePermission';
import { useSnackbar } from '../../components/SnackbarProvider';
import {
  createArticle,
  deleteArticle,
  setSelectedArticle,
} from '../../reduxStore/ArticleSlice';
import { Article, FamilleData, Tiers } from '../../models/models';
import FamilleAutoC from '../AutoComplete/FamilleAutoC';
import FournisseurAutoC from '../AutoComplete/FournisseurAutoC';
import { labels } from '../../constants/labels';
import SelectDepartementsPartage, { parsePartageIds } from '../Selects/SelectDepartementsPartage';
import { fetchFamilleDefaut } from '../../reduxStore/FamilleDefautSlice';
import { useOption } from '../../hooks/useOption';
import { OptionKey } from '../../constants/enums';
import { RootState } from '../../reduxStore/Store';
import { FamilleDefautComplet } from '../../models/models';
import FamilleDefautPropositionDialog, { PropositionFamille } from './FamilleDefautPropositionDialog';

/** Champs « valeur simple » proposables depuis la famille (migration 0136). */
const CHAMPS_FAMILLE: { key: keyof Article; label: string }[] = [
  { key: 'prix_v',            label: 'Prix de vente' },
  { key: 'prix_r',            label: 'Prix de revient' },
  { key: 'remise',            label: 'Remise max (%)' },
  { key: 'tva',               label: 'TVA (%)' },
  { key: 'unite',             label: 'Unité' },
  { key: 'unite_sec',         label: 'Unité secondaire' },
  { key: 'pieces_par_carton', label: 'Pièces par carton' },
  { key: 'stock_securite',    label: 'Stock de sécurité' },
  { key: 'track_stock',       label: 'Suivi du stock' },
];
const affiche = (v: any) =>
  v === null || v === undefined || v === '' ? '—' : typeof v === 'boolean' ? (v ? 'oui' : 'non') : String(v);

/** Nom affiché d'un tiers — même expression que fournisseur_nom côté SP (nom + prénom). */
const nomTiers = (t: Tiers) => [t.nom, t.prenom].filter(Boolean).join(' ');

/** Tiers minimal pour FournisseurAutoC à partir d'un couple id / nom retourné par une SP. */
const tiersDepuisNom = (id?: number | null, nom?: string | null): Tiers | null =>
  id ? ({ id, nom: nom ?? `#${id}`, prenom: '', type: TypeTiers.FOURNISSEUR } as Tiers) : null;

interface ArticleFormProps {
  onCancel: () => void;
}

const ArticleForm: React.FC<ArticleFormProps> = ({ onCancel }) => {
  const { selectedArticle } = useSelector((state: any) => state.ArticleSlice);
  const dispatch = useDispatch<AppDispatch>();
  const { showSnackbar } = useSnackbar();

  const [selectedFamille, setSelectedFamille] = useState<FamilleData | null>(null);
  const [selectedFournisseur, setSelectedFournisseur] = useState<Tiers | null>(null);

  const initialForm = React.useMemo(() => ({
    id: -1,
    code: '',
    designation: '',
    prix_r: 0,
    prix_v: 0,
    stock_securite: 0,
    track_stock: true,
    visible: true,
    tva: 0,
    remise: 0,
    poids: 0,
    unite: null,
    unite_sec: null,
    pieces_par_carton: 1,
    allow_pcs_cts: false,
    famille_id: null,
    departements_partage: [] as number[],
  }), []);

  const [formData, setFormData] = useState<Article>(initialForm);
  // Copie des prix département de la famille : droit prixDepartements.update (0154)
  const peutModifierPrixDept = usePermission(PERMISSION_PRIX_DEPT.UPDATE);
  const voitCout = usePermission(PERMISSION_PRIX_REVIENT);   // 0156

  // Option 0138 : à la création depuis un département, PV / PR réservés à ce département
  const prixCreationDept = useOption<boolean>(OptionKey.ARTICLE_PRIX_CREATION_DEPT, false);
  const departementActif = useSelector((state: RootState) => state.AppSlice.selectedDepartement);
  const infoPrixDept = !selectedArticle && prixCreationDept && !!departementActif;
  // « Tous départements » (0142) : prix saisis réservés aux départements cochés (fiche à 0) ;
  // aucun ou tous cochés → prix saisis sur la fiche. Même règle que ArticleService côté API.
  const infoPrixTous = !selectedArticle && prixCreationDept && !departementActif;
  const departementsVisibles = useSelector((state: RootState) => state.DepartementSlice.departements);
  const partage = formData.departements_partage ?? [];
  const tousCoches = departementsVisibles.length > 0
    && departementsVisibles.every((d) => d.id !== undefined && partage.includes(d.id));
  const nomsDeptsPrix = infoPrixTous && !tousCoches
    ? departementsVisibles.filter((d) => d.id !== undefined && partage.includes(d.id)).map((d) => d.nom)
    : [];

  // ── Proposition des valeurs de la famille (migration 0136) ──
  const [propositionFamille, setPropositionFamille] =
    useState<{ famille: FamilleData; data: FamilleDefautComplet; propositions: PropositionFamille[] } | null>(null);

  /** Valeur que la famille appliquerait à un champ (null = rien à proposer). */
  const valeurFamille = (fam: FamilleDefautComplet, key: keyof Article): any => {
    const v = fam.defaut ? (fam.defaut as any)[key] : null;
    if (v === null || v === undefined || v === '') return null;
    if (key === 'track_stock') return !!Number(v);
    if (key === 'unite' || key === 'unite_sec') return String(v);
    return Number(v);
  };

  /** À la sélection d'une famille : propose ses valeurs (case par valeur), sans rien imposer. */
  const proposerValeursFamille = async (famille: FamilleData) => {
    try {
      const fam = await dispatch(fetchFamilleDefaut(famille.id)).unwrap();
      const propositions: PropositionFamille[] = [];
      for (const { key, label } of CHAMPS_FAMILLE) {
        const v = valeurFamille(fam, key);
        if (v === null || v === (formData as any)[key]) continue;
        propositions.push({ key, label, valeur: `${affiche(v)} (actuel : ${affiche((formData as any)[key])})` });
      }
      // Fournisseur par défaut de la famille (0143)
      const fournFamille = fam.defaut?.fournisseur_id ?? null;
      if (fournFamille && fournFamille !== formData.fournisseur_id) {
        propositions.push({
          key: 'fournisseur', label: labels.fournisseur,
          valeur: `${affiche(fam.defaut?.fournisseur_nom)} (actuel : ${affiche(formData.fournisseur_nom)})`,
        });
      }
      if (fam.departements_partage_ids) {
        propositions.push({ key: 'partage', label: 'Départements', valeur: fam.departements_partage_noms ?? '' });
      }
      if (peutModifierPrixDept && fam.prix_departements.length > 0) {
        propositions.push({
          key: 'prix_departements', label: 'Prix par département',
          valeur: fam.prix_departements.map((p) => p.departement_nom).join(', '),
        });
      }
      if (propositions.length > 0) setPropositionFamille({ famille, data: fam, propositions });
    } catch {
      // Valeurs de famille indisponibles : l'attribution de la famille reste valable
    }
  };

  const appliquerValeursFamille = (keys: string[]) => {
    if (!propositionFamille) return;
    const fam = propositionFamille.data;
    setFormData((prev) => {
      const next: Article = { ...prev };
      for (const { key } of CHAMPS_FAMILLE) {
        if (keys.includes(key as string)) (next as any)[key] = valeurFamille(fam, key);
      }
      if (keys.includes('partage')) next.departements_partage = parsePartageIds(fam.departements_partage_ids);
      if (keys.includes('fournisseur') && fam.defaut?.fournisseur_id) {
        next.fournisseur_id = fam.defaut.fournisseur_id;
        next.fournisseur_nom = fam.defaut.fournisseur_nom ?? null;
      }
      // Prix département : copiés côté serveur à l'enregistrement (ArticleService._save)
      next.appliquer_prix_dept_famille = keys.includes('prix_departements');
      return next;
    });
    if (keys.includes('fournisseur') && fam.defaut?.fournisseur_id) {
      setSelectedFournisseur(tiersDepuisNom(fam.defaut.fournisseur_id, fam.defaut.fournisseur_nom));
    }
    setPropositionFamille(null);
  };

  useEffect(() => {
    if (selectedArticle) {
      // Mappe les valeurs DÉFAUT (brutes article.*) plutôt que les effectives
      // (qui peuvent contenir l'override dept courant). Sinon une simple
      // sauvegarde sans modification écraserait silencieusement le défaut
      // tenant avec le prix override du dept courant.
      // Les *_defaut sont retournés par sp_select_articleByFilters mode `article`
      // (cf. migration 0026). Fallback sur les effectifs pour rétro-compat.
      setFormData({
        ...selectedArticle,
        prix_v: selectedArticle.prix_v_defaut ?? selectedArticle.prix_v,
        prix_r: selectedArticle.prix_r_defaut ?? selectedArticle.prix_r,
        remise: selectedArticle.remise_defaut ?? selectedArticle.remise,
        departements_partage: parsePartageIds(selectedArticle.departements_partage_ids),
      });
      setSelectedFamille(
        selectedArticle.famille_id
          ? { id: selectedArticle.famille_id, famille_row_id: 0, nom: selectedArticle.famille_nom ?? '', visible: true } as FamilleData
          : null
      );
      // Valeur initiale depuis fournisseur_nom : s'affiche même si le fournisseur
      // n'est pas visible dans le département courant (absent de l'autocomplete)
      setSelectedFournisseur(tiersDepuisNom(selectedArticle.fournisseur_id, selectedArticle.fournisseur_nom));
    } else {
      setFormData(initialForm);
      setSelectedFamille(null);
      setSelectedFournisseur(null);
    }
  }, [initialForm, selectedArticle]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const val = type === 'number' ? Number(value) : value;
    setFormData((prev) => ({
      ...prev,
      [name]: val,
    }));
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const resetForm = () => {
    setFormData(initialForm);
    dispatch(setSelectedArticle(null));
  };

  const handleSubmit = async () => {
    try {
        console.log('FormData:', formData); // Log the formData to check its structure
      await dispatch(createArticle(formData)).unwrap();
      showSnackbar('Article enregistré avec succès !', 'success');
      resetForm();
    } catch (error) {
      showSnackbar(error as string || 'Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async () => {
    if (selectedArticle && window.confirm('Supprimer cet article ?')) {
      try {
        await dispatch(deleteArticle(selectedArticle.id!)).unwrap();
        showSnackbar('Article supprimé', 'success');
        resetForm();
      } catch (error) {
        showSnackbar(error as string || 'Erreur lors de la suppression', 'error');
      }
    }
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginBottom: 2 }}>
      <Typography variant="h6" gutterBottom>
        {selectedArticle ? 'Modifier un Article' : 'Ajouter un Article'}
      </Typography>
      {infoPrixDept && (
        <Typography variant="body2" sx={{ color: '#667085', mb: 1.5 }}>
          Prix de vente et de revient enregistrés pour le département « {departementActif?.nom} » —
          0 dans les autres départements.
        </Typography>
      )}
      {infoPrixTous && (
        <Typography variant="body2" sx={{ color: '#667085', mb: 1.5 }}>
          {nomsDeptsPrix.length > 0
            ? <>Prix de vente et de revient enregistrés pour « {nomsDeptsPrix.join(', ')} » — 0 sur la
                fiche et dans les autres départements.</>
            : <>Prix de vente et de revient enregistrés sur la fiche — appliqués à tous les départements.</>}
        </Typography>
      )}
      <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
        {/* Identification */}
        <TextField name="code" label="Code" value={formData.code} onChange={handleChange} fullWidth size="small" />
        <TextField name="designation" label="Désignation" value={formData.designation} onChange={handleChange} fullWidth size="small" />
        {/* Prix */}
        {/* Au focus : valeur sélectionnée → la saisie remplace directement l'ancien prix */}
        {/* Sans le droit de voir le coût : champ absent, l'API garde le prix de revient en place */}
        {voitCout && (
          <TextField name="prix_r" label="Prix Réel" type="number" value={formData.prix_r} onChange={handleChange} onFocus={(e) => e.target.select()} fullWidth size="small" />
        )}
        <TextField name="prix_v" label="Prix Vente HT" type="number" value={formData.prix_v} onChange={handleChange} onFocus={(e) => e.target.select()} fullWidth size="small" />
        {/* Fiscal */}
        <TextField name="tva" label="TVA (%)" type="number" value={formData.tva} onChange={handleChange} fullWidth size="small" />
        <TextField name="remise" label="Remise (%)" type="number" value={formData.remise} onChange={handleChange} fullWidth size="small" />
        {/* Physique */}
        <TextField name="poids" label="Poids" type="number" value={formData.poids} onChange={handleChange} fullWidth size="small" />
        <TextField name="stock_securite" label="Stock Sécurité" type="number" value={formData.stock_securite} onChange={handleChange} fullWidth size="small" />
        {/* Unités */}
        <TextField name="unite" label="Unité" value={formData.unite || ''} onChange={handleChange} fullWidth size="small" />
        <TextField name="unite_sec" label="Unité secondaire" value={formData.unite_sec || ''} onChange={handleChange} fullWidth size="small" />
        <TextField name="pieces_par_carton" label="Pièces par carton" type="number" value={formData.pieces_par_carton ?? 1} onChange={handleChange} fullWidth size="small" />
        {/* Famille */}
        <Box>
          <FamilleAutoC
            slot="main"
            type={GeneriqueDataType.FAMILLE_ARTICLE}
            label="Famille"
            selectedFamille={selectedFamille}
            onFamilleChange={(f) => {
              const changement = !!f && f.id !== formData.famille_id;
              setSelectedFamille(f);
              setFormData((prev) => ({ ...prev, famille_id: f ? f.id : null, appliquer_prix_dept_famille: false }));
              if (f && changement) proposerValeursFamille(f);
            }}
          />
        </Box>
        {/* Partage par département — vide = tous */}
        <SelectDepartementsPartage
          value={formData.departements_partage ?? []}
          onChange={(ids) => setFormData((prev) => ({ ...prev, departements_partage: ids }))}
        />
        {/* Fournisseur principal (0143) — facultatif ; effacé → null = retiré */}
        <FournisseurAutoC
          selectedFournisseur={selectedFournisseur}
          onFournisseurChange={(t) => {
            setSelectedFournisseur(t);
            setFormData((prev) => ({ ...prev, fournisseur_id: t ? t.id : null, fournisseur_nom: t ? nomTiers(t) : null }));
          }}
        />
        {/* Toggles groupés */}
        <Box sx={{ gridColumn: 'span 2', display: 'flex', gap: 4, flexWrap: 'wrap', pt: 1 }}>
          <FormControlLabel
            control={<Switch checked={formData.track_stock} onChange={handleToggle} name="track_stock" />}
            label="Suivi du stock"
          />
          <FormControlLabel
            control={<Switch checked={formData.allow_pcs_cts} onChange={handleToggle} name="allow_pcs_cts" />}
            label="Vente pièces/cartons"
          />
          <FormControlLabel
            control={<Switch checked={formData.visible} onChange={handleToggle} name="visible" />}
            label="Visible"
          />
        </Box>
      </Box>
      <Box mt={2} display="flex" justifyContent="space-between">
        <div>
          <MyButton actionType={ButtonType.INSERT} variant="contained" onClick={handleSubmit}>
            {selectedArticle ? 'Modifier' : 'Ajouter'}
          </MyButton>
            <MyButton actionType={ButtonType.DELETE} variant="outlined" onClick={handleDelete}>
              Supprimer
            </MyButton>
          <MyButton actionType={ButtonType.CANCEL} variant="outlined" onClick={onCancel}>
            Annuler
          </MyButton>
        </div>

      </Box>

      {/* Proposition des valeurs de la famille (migration 0136) */}
      <FamilleDefautPropositionDialog
        open={!!propositionFamille}
        familleNom={propositionFamille?.famille.nom ?? ''}
        propositions={propositionFamille?.propositions ?? []}
        onClose={() => setPropositionFamille(null)}
        onAppliquer={appliquerValeursFamille}
      />
    </Paper>
  );
};

export default ArticleForm;
